import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { PlusIcon } from "@radix-ui/react-icons";

interface SelectOrCreateProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  word?: {
    singular: string;
    plural: string;
  };
  hint?: string;
}

export function SelectOrCreate({
  value,
  onChange,
  options,
  word = {
    singular: "option",
    plural: "options",
  },
  hint,
}: SelectOrCreateProps) {
  const [inputValue, setInputValue] = useState(value);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const uniqueOptions = useMemo(() => [...new Set(options)], [options]);

  const suggestions = useMemo(
    () =>
      uniqueOptions.filter((option) =>
        option.toLowerCase().includes((inputValue || "").toLowerCase())
      ),
    [uniqueOptions, inputValue]
  );

  const isNewOption = useMemo(() => {
    return (
      inputValue?.trim() &&
      !uniqueOptions.some(
        (option) => option.toLowerCase() === inputValue.trim().toLowerCase()
      )
    );
  }, [inputValue, uniqueOptions]);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
      setShowSuggestions(true);
    },
    []
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && inputValue?.trim()) {
        onChange(inputValue.trim());
        setShowSuggestions(false);
      } else if (e.key === "Escape") {
        setShowSuggestions(false);
      }
    },
    [inputValue, onChange]
  );

  const handleSuggestionClick = useCallback(
    (suggestion: string) => {
      onChange(suggestion);
      setInputValue(suggestion);
      setShowSuggestions(false);
    },
    [onChange]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative w-full">
      <Input
        ref={inputRef}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setShowSuggestions(true)}
        placeholder={`Type or select a ${word.singular}`}
      />
      {(showSuggestions || document.activeElement === inputRef.current) && (
        <div
          ref={suggestionsRef}
          className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-[200px] overflow-y-auto"
        >
          {!inputValue?.trim() ? (
            <>
              <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50">
                Existing {word.plural}
              </div>

              {uniqueOptions.map((option) => (
                <div
                  key={option}
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                  onClick={() => handleSuggestionClick(option)}
                >
                  {option}
                </div>
              ))}
            </>
          ) : (
            <>
              {isNewOption && (
                <div
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm border-b border-gray-200 flex items-center gap-2 text-blue-600"
                  onClick={() => handleSuggestionClick(inputValue.trim())}
                >
                  <PlusIcon className="w-4 h-4" />
                  <span>Create "{inputValue.trim()}"</span>
                </div>
              )}

              {suggestions.map((suggestion) => (
                <div
                  key={suggestion}
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion}
                </div>
              ))}

              {!isNewOption && suggestions.length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-500">
                  No matching {word.plural} found
                </div>
              )}
            </>
          )}

          {hint && (
            <div
              className="px-3 py-2 text-sm border-t border-gray-200"
              style={{
                background: "#EFF6FF",
                borderColor: "#BFDBFE",
                color: "#1E40AF",
              }}
            >
              {hint}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
